import { memo, useCallback, useEffect, useMemo } from 'react';
import { SidebarBoxKey } from 'AdmissionsGuidance';
import { useTranslation } from 'react-i18next';
import { type ExecEdCertCategory, type ExecEdEligibilityBundle } from 'ExecEdEligibility';
import { useAngularContext } from 'AngularContext';
import { showExecEdEligibilityBundle } from 'AdmissionsGuidance/showExecEdEligibilityBundle';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentUser, useOfflineModeManager } from 'FrontRoyalAngular';
import { getHasActivatedExecEdEligibilityBundle, getProgramApplication, getUserProgramState } from 'Users';
import { ProgramTypeConfigs } from 'Program';
import { isPendingProgramApplication } from 'ProgramApplication';
import { ExecEdCertCategoryConfigs } from 'ExecEdEligibility/helpers/ExecEdCategories';
import { Text } from 'Text';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHexagonCheck } from '@fortawesome/pro-light-svg-icons';
import { studentDashboardActions, getHasSeenExecEdEligibilityModal } from 'StudentDashboardSlice';
import { LogoBox } from '../../LogoBox';
import { type ButtonConfig } from '../../StudentDashboardSidebar.types';
import { BasicMobileBox } from '../../BasicMobileBox';

type Props = {
    filteredEligibilityBundle: ExecEdEligibilityBundle;
    mobile?: boolean;
};

function PreApprovedForCohortBoxComponent({ filteredEligibilityBundle, mobile }: Props) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'preApprovedForCohortBox' });
    const $injector = useAngularContext();
    const currentUser = useCurrentUser();
    const dispatch = useDispatch();
    const offline = useOfflineModeManager().inOfflineMode;
    const hasSeenExecEdEligibilityModalThisPageLoad = useSelector(getHasSeenExecEdEligibilityModal);
    const bundleActivated = useMemo(() => getHasActivatedExecEdEligibilityBundle(currentUser), [currentUser]);
    const ups = useMemo(() => getUserProgramState(currentUser), [currentUser]);
    const isCurrentlyExecEdUps = useMemo(() => ups?.programType && ProgramTypeConfigs[ups.programType].isExecEd, [ups]);
    const programApplication = useMemo(() => getProgramApplication(currentUser), [currentUser]);
    const hasPendingApplication = useMemo(
        () => !!programApplication && isPendingProgramApplication(programApplication),
        [programApplication],
    );

    const categoriesEligibleFor = useMemo(() => {
        const categories: ExecEdCertCategory[] = [];
        const eligibleProgramTypes = filteredEligibilityBundle?.eligiblePrograms.map(p => p.programType);

        Object.entries(ExecEdCertCategoryConfigs).forEach(([category, config]) => {
            if (config.programTypes.some(p => eligibleProgramTypes?.includes(p))) {
                categories.push(category as ExecEdCertCategory);
            }
        });

        return categories;
    }, [filteredEligibilityBundle]);

    const openCongratsModal = useCallback(() => {
        showExecEdEligibilityBundle($injector);
        dispatch(studentDashboardActions.setHasSeenExecEdEligibilityModal(true));
    }, [$injector, dispatch]);

    const buttons = useMemo(
        () => [
            {
                id: 'button_view_programs',
                text: t('review_and_register'),
                onPress: () => openCongratsModal(),
            },
        ],
        [openCongratsModal, t],
    );

    useEffect(() => {
        const shouldAutomaticallyShowModal =
            !offline &&
            bundleActivated &&
            !hasPendingApplication &&
            !isCurrentlyExecEdUps &&
            (filteredEligibilityBundle.eligiblePrograms.length > 0 ||
                filteredEligibilityBundle.recommendedProgramType) &&
            !hasSeenExecEdEligibilityModalThisPageLoad;

        if (shouldAutomaticallyShowModal) {
            openCongratsModal();
        }
    }, [
        bundleActivated,
        filteredEligibilityBundle,
        hasPendingApplication,
        hasSeenExecEdEligibilityModalThisPageLoad,
        isCurrentlyExecEdUps,
        offline,
        openCongratsModal,
    ]);

    return mobile ? (
        <PreApprovedForCohortBoxMobilePresentation onPress={openCongratsModal} />
    ) : (
        <PreApprovedForCohortBoxPresentation categoriesEligibleFor={categoriesEligibleFor} buttons={buttons} />
    );
}

function PreApprovedForCohortBoxMobilePresentation({ onPress }: { onPress: () => void }) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'preApprovedForCohortBox' });
    return (
        <BasicMobileBox
            boxKey={SidebarBoxKey.pre_approved_for_cohort}
            buttonId="button_view_programs"
            heading={t('mobileTitle')}
            ctaText={t('review_and_register')!}
            onPress={onPress}
        />
    );
}

function PreApprovedForCohortBoxPresentation({
    categoriesEligibleFor,
    buttons,
}: {
    categoriesEligibleFor: ExecEdCertCategory[];
    buttons: ButtonConfig[];
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'preApprovedForCohortBox' });
    return (
        <LogoBox
            title={t('title')}
            boxKey={SidebarBoxKey.pre_approved_for_cohort}
            color="primary"
            buttons={buttons}
            // The ExecEdEligibility box is always branded with the Quantic logo.
            forceQuantic
        >
            {t('content')}
            <div className="mt-4">
                {categoriesEligibleFor.map(category => (
                    <Text
                        as="p"
                        key={category}
                        color="black"
                        weight="semibold"
                        className="mb-0.5 flex items-center justify-start last:mb-0"
                    >
                        <FontAwesomeIcon icon={faHexagonCheck} className="-mt-[1px] me-2 text-xs" />
                        {t(category)}
                    </Text>
                ))}
            </div>
        </LogoBox>
    );
}

export const PreApprovedForCohortBox = memo(
    PreApprovedForCohortBoxComponent,
) as typeof PreApprovedForCohortBoxComponent;
